/* ==========================================================================
   Ecoline — product.js (PDP owner) — v2.
   Behavior for sections/main-product.liquid:
   - thumbnail click swaps main media
   - finish pill click selects variant (URL ?variant=, price card incl.
     long-form savings + image discount badge, ATC state, SKU, media
     filtered by alt prefix "Finish: <name>")
   - qty stepper; add to cart via Ecoline.cart.add() then
     Ecoline.drawer.open('cart'); Buy now adds then redirects to /checkout
   - pincode checker via Ecoline.pincode (spec §5)
   - sticky mobile ATC bar reveal once the buy box scrolls out of view
   PDP accordions are native <details> handled by assets/accordion.js.
   Contracts per DESIGN-SPEC §5 (window.Ecoline is owned by Foundation).
   ========================================================================== */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  function fm(cents) {
    if (window.Ecoline && window.Ecoline.formatMoney) return window.Ecoline.formatMoney(cents);
    return '₹' + (Math.round(cents) / 100).toLocaleString('en-IN');
  }

  /* Extract finish name from a media alt following the convention
     "Finish: <FinishName> | <desc>". Returns '' when no prefix. */
  function finishOfAlt(alt) {
    var m = /^finish:\s*([^|]+?)\s*(?:\||$)/i.exec(alt || '');
    return m ? m[1].trim() : '';
  }

  function initPdp(root) {
    if (!root || root.elPdpInit) return;
    root.elPdpInit = true;

    /* ---------------- data ---------------- */
    var variants = [];
    var jsonEl = root.querySelector('[data-pdp-variants]');
    if (jsonEl) {
      try { variants = JSON.parse(jsonEl.textContent) || []; } catch (e) { variants = []; }
    }
    var finishPos = parseInt(root.getAttribute('data-finish-position'), 10) || 0;
    var productUrl = root.getAttribute('data-product-url') || window.location.pathname;
    var showMrp = root.getAttribute('data-show-mrp') !== 'false';

    var current = null;
    var selectedId = parseInt(root.getAttribute('data-selected-variant'), 10);
    variants.forEach(function (v) { if (v.id === selectedId) current = v; });
    if (!current && variants.length) current = variants[0];

    /* ---------------- elements ---------------- */
    var mediaItems = Array.prototype.slice.call(root.querySelectorAll('[data-media-item]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-media-thumb]'));
    var thumbRow = root.querySelector('[data-thumb-row]');
    var finishBtns = Array.prototype.slice.call(root.querySelectorAll('[data-finish-value]'));
    var finishLabel = root.querySelector('[data-finish-label]');
    var skuEl = root.querySelector('[data-pdp-sku]');
    var variantInput = root.querySelector('[data-variant-input]');
    var priceAmount = root.querySelector('[data-pdp-price] .el-price__amount');
    var priceCompare = root.querySelector('[data-pdp-price] .el-price__compare');
    var priceOff = root.querySelector('[data-pdp-price] .el-price__off');
    var stickyPrice = root.querySelector('[data-sticky-price]');
    var stickyCompare = root.querySelector('[data-sticky-compare]');
    var atcBtns = Array.prototype.slice.call(root.querySelectorAll('[data-atc]'));
    var buyNowBtn = root.querySelector('[data-buy-now]');
    var atcError = root.querySelector('[data-atc-error]');
    var offBadge = root.querySelector('[data-off-badge]');
    var qtyInput = root.querySelector('[data-qty-input]');
    var qtyDec = root.querySelector('[data-qty-dec]');
    var qtyInc = root.querySelector('[data-qty-inc]');
    var stickyBar = root.querySelector('[data-sticky-bar]');
    var buybox = root.querySelector('[data-buybox]');

    function activeFinish() {
      if (!current || !finishPos) return '';
      return current['option' + finishPos] || '';
    }

    /* ---------------- gallery ---------------- */
    function setActiveMedia(index) {
      index = String(index);
      mediaItems.forEach(function (item) {
        item.classList.toggle('is-active', item.getAttribute('data-media-index') === index);
      });
      thumbs.forEach(function (t) {
        t.classList.toggle('is-active', t.getAttribute('data-media-index') === index);
        t.setAttribute('aria-current', t.getAttribute('data-media-index') === index ? 'true' : 'false');
      });
    }

    function filterMedia() {
      if (!mediaItems.length) return;
      var finish = activeFinish().toLowerCase();
      var visible = [];
      mediaItems.forEach(function (item) {
        var f = finishOfAlt(item.getAttribute('data-media-alt')).toLowerCase();
        var show = !finish || !f || f === finish;
        item.classList.toggle('is-filtered', !show);
        if (show) visible.push(item);
      });
      /* never end up with an empty gallery */
      if (!visible.length) {
        mediaItems.forEach(function (item) { item.classList.remove('is-filtered'); });
        visible = mediaItems.slice();
      }
      thumbs.forEach(function (t) {
        var idx = t.getAttribute('data-media-index');
        var hidden = true;
        visible.forEach(function (item) {
          if (item.getAttribute('data-media-index') === idx) hidden = false;
        });
        t.classList.toggle('is-filtered', hidden);
      });
      var hasActive = visible.some(function (item) { return item.classList.contains('is-active'); });
      if (!hasActive) {
        /* Prefer the first visible PHOTO — a "| dims" alt marks the technical
           dimension diagram, which should never become the hero image just
           because it sorts first for this finish. */
        var firstPhoto = null;
        visible.forEach(function (item) {
          if (firstPhoto) return;
          var alt = (item.getAttribute('data-media-alt') || '').toLowerCase();
          if (alt.indexOf('| dims') === -1) firstPhoto = item;
        });
        setActiveMedia((firstPhoto || visible[0]).getAttribute('data-media-index'));
      }
      if (thumbRow) {
        if (visible.length < 2) thumbRow.setAttribute('hidden', '');
        else thumbRow.removeAttribute('hidden');
      }
    }

    thumbs.forEach(function (t) {
      t.addEventListener('click', function () {
        setActiveMedia(t.getAttribute('data-media-index'));
      });
    });

    /* ---------------- price + buy state ---------------- */
    function updatePrice(v) {
      /* hasCompareAtAll ignores the show_mrp setting: it's "does this variant
         actually have a compare-at price", which is what decides whether the
         pill can ever read "Factory-direct price" (snippets/price.liquid:
         shown whenever compare_at_price is blank, independent of show_mrp). */
      var hasCompareAtAll = !!(v.compare_at_price && v.compare_at_price > v.price);
      var hasCompare = hasCompareAtAll && showMrp;
      /* round to nearest — matches the Amazon-verified figure behind the
         sitewide "Up to 67% off MRP" claim (book rack 999 vs 2999 = 66.7%
         -> 67%, BUILD-DECISIONS #3/#10; matches snippets/price.liquid +
         main-product.liquid + the mega menu). */
      var pct = hasCompare ? Math.round((1 - v.price / v.compare_at_price) * 100) : 0;
      if (priceAmount) priceAmount.textContent = fm(v.price);
      if (priceCompare) {
        if (hasCompare) {
          priceCompare.textContent = 'MRP ' + fm(v.compare_at_price);
          priceCompare.removeAttribute('hidden');
        } else {
          priceCompare.setAttribute('hidden', '');
        }
      }
      if (priceOff) {
        if (hasCompare && pct > 0) {
          /* DESIGN's long-form savings line (offLong): "40% off · you save ₹1,600" */
          priceOff.textContent = pct + '% off · you save ' + fm(v.compare_at_price - v.price);
          priceOff.removeAttribute('hidden');
        } else if (!hasCompareAtAll) {
          priceOff.textContent = 'Factory-direct price';
          priceOff.removeAttribute('hidden');
        } else {
          /* has a compare-at price, but show_mrp is off (or 0%): show neither */
          priceOff.setAttribute('hidden', '');
        }
      }
      if (offBadge) {
        if (hasCompare && pct > 0) {
          offBadge.textContent = pct + '% OFF';
          offBadge.removeAttribute('hidden');
        } else {
          offBadge.setAttribute('hidden', '');
        }
      }
      if (stickyPrice) stickyPrice.textContent = fm(v.price);
      if (stickyCompare) {
        if (hasCompare) {
          stickyCompare.textContent = fm(v.compare_at_price);
          stickyCompare.removeAttribute('hidden');
        } else {
          stickyCompare.setAttribute('hidden', '');
        }
      }
    }

    function updateBuyState() {
      /* A variant with no price at all is a "coming soon" draft — it was
         never on sale, so the CTA must not claim "Sold out" (matches the
         PLP card + the SSR state in main-product.liquid). */
      var pricePending = !!(current && (current.price == null || current.price === ''));
      var available = !!(current && current.available) && !pricePending;
      var soldLabel = pricePending ? 'Coming soon' : 'Sold out';
      atcBtns.forEach(function (b) {
        b.disabled = !available;
        if (b.hasAttribute('data-atc-main')) {
          b.textContent = available ? 'Add to cart — ' + fm(current.price) : soldLabel;
        } else {
          b.textContent = available ? 'Add to cart' : soldLabel;
        }
      });
      if (buyNowBtn) buyNowBtn.disabled = !available;
      if (variantInput && current) variantInput.value = current.id;
    }

    /* ---------------- finish / variant selection ---------------- */
    function selectVariant(v) {
      if (!v) return;
      current = v;
      updatePrice(v);
      updateBuyState();
      /* always sync (not just when truthy) so switching to/from a SKU-less
         variant doesn't leave a stale SKU on screen; the "SKU " prefix lives
         inside the span so it disappears along with the SKU when blank */
      if (skuEl) skuEl.textContent = v.sku ? 'SKU ' + v.sku : '';
      if (finishPos) {
        var name = v['option' + finishPos] || '';
        if (finishLabel) finishLabel.textContent = name;
        finishBtns.forEach(function (b) {
          var on = b.getAttribute('data-finish-value') === name;
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
          var sw = b.querySelector('.el-swatch');
          if (sw) sw.classList.toggle('is-selected', on);
        });
        filterMedia();
      }
      try {
        var url = productUrl.split('?')[0] + '?variant=' + v.id;
        window.history.replaceState({}, '', url);
      } catch (e) { /* noop */ }
    }

    /* True when v agrees with the currently selected variant on every option
       position except finishPos — i.e. it's the same Size/etc, just a
       different finish. Falls back to true when there's no current variant
       to compare against. */
    function optionsMatchCurrent(v) {
      if (!current) return true;
      for (var i = 1; i <= 3; i++) {
        if (i === finishPos) continue;
        if (v['option' + i] !== current['option' + i]) return false;
      }
      return true;
    }

    finishBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!finishPos) return;
        var value = btn.getAttribute('data-finish-value');
        var strictMatch = null;
        var strictFallback = null;
        var looseMatch = null;
        var looseFallback = null;
        variants.forEach(function (v) {
          if (v['option' + finishPos] !== value) return;
          if (!looseFallback) looseFallback = v;
          if (!looseMatch && v.available) looseMatch = v;
          if (optionsMatchCurrent(v)) {
            if (!strictFallback) strictFallback = v;
            if (!strictMatch && v.available) strictMatch = v;
          }
        });
        /* prefer a variant that keeps every other option (e.g. Size)
           unchanged; only fall back to a finish-only match (which may
           silently switch other options) when no such variant exists */
        selectVariant(strictMatch || strictFallback || looseMatch || looseFallback);
      });
    });

    /* ---------------- quantity stepper ---------------- */
    function currentQty() {
      var q = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      if (isNaN(q) || q < 1) q = 1;
      if (q > 99) q = 99;
      return q;
    }

    function stepQty(delta) {
      if (!qtyInput) return;
      var q = currentQty() + delta;
      if (q < 1) q = 1;
      if (q > 99) q = 99;
      qtyInput.value = q;
    }

    if (qtyDec) qtyDec.addEventListener('click', function () { stepQty(-1); });
    if (qtyInc) qtyInc.addEventListener('click', function () { stepQty(1); });
    if (qtyInput) {
      qtyInput.addEventListener('change', function () { qtyInput.value = currentQty(); });
    }

    /* ---------------- add to cart / buy now ---------------- */
    var busy = false;

    function setBusy(on) {
      busy = on;
      var all = atcBtns.slice();
      if (buyNowBtn) all.push(buyNowBtn);
      all.forEach(function (b) {
        b.classList.toggle('is-loading', on);
        b.disabled = on || !(current && current.available);
      });
    }

    function clearAtcError() {
      if (!atcError) return;
      atcError.textContent = '';
      atcError.setAttribute('hidden', '');
    }

    function showAtcError(err) {
      if (!atcError) return;
      var msg = (err && err.data && err.data.description) || 'Couldn’t add to cart — please retry';
      atcError.textContent = msg;
      atcError.removeAttribute('hidden');
    }

    function addToCart(thenCheckout) {
      if (busy || !current || !current.available) return;
      if (!(window.Ecoline && window.Ecoline.cart)) return;
      setBusy(true);
      clearAtcError();
      window.Ecoline.cart.add(current.id, currentQty())
        .then(function () {
          setBusy(false);
          if (thenCheckout) {
            window.location = '/checkout';
          } else if (window.Ecoline.drawer) {
            window.Ecoline.drawer.open('cart');
          }
        })
        .catch(function (err) {
          setBusy(false);
          showAtcError(err);
        });
    }

    atcBtns.forEach(function (b) {
      b.addEventListener('click', function (e) {
        /* the main ATC is a real submit button inside {% form 'product' %}
           with a working name="id" input — only hijack its native POST
           /cart/add when the cart API is actually available, so a failed or
           late-loading app.js still lets the form submit instead of leaving
           a dead button */
        if (!(window.Ecoline && window.Ecoline.cart)) return;
        e.preventDefault();
        addToCart(false);
      });
    });
    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', function (e) {
        /* Buy now is a real submit (name="checkout") inside {% form
           'product' %} — Shopify routes that POST /cart/add straight to
           checkout. Only hijack it when the cart API is actually loaded,
           mirroring the ATC fallback above, so JS-off / failed app.js
           still gets a working native flow. */
        if (!(window.Ecoline && window.Ecoline.cart)) return;
        e.preventDefault();
        addToCart(true);
      });
    }

    /* ---------------- pincode checker ---------------- */
    var pinForm = root.querySelector('[data-pincode-form]');
    var pinInput = root.querySelector('[data-pincode-input]');
    var pinResult = root.querySelector('[data-pincode-result]');

    function renderPin(res) {
      if (!pinResult) return;
      pinResult.removeAttribute('hidden');
      pinResult.classList.toggle('is-error', !res.valid);
      if (res.valid) {
        /* res.message = "✓ <pin> — Delivers in 4–6 days." (digits only, safe) */
        pinResult.innerHTML = '<b>' + res.message + '</b> ' + res.note;
      } else {
        pinResult.textContent = res.message;
      }
    }

    if (pinForm && pinInput) {
      pinInput.addEventListener('input', function () {
        pinInput.value = pinInput.value.replace(/[^0-9]/g, '').slice(0, 6);
      });
      pinForm.addEventListener('submit', function (e) {
        e.preventDefault();
        if (window.Ecoline && window.Ecoline.pincode) {
          renderPin(window.Ecoline.pincode.validate(pinInput.value));
        }
      });
      /* restore last checked pin (localStorage `ecoline_pin`, spec §5) */
      if (window.Ecoline && window.Ecoline.pincode) {
        var saved = window.Ecoline.pincode.saved();
        if (saved) {
          pinInput.value = saved;
          renderPin(window.Ecoline.pincode.validate(saved));
        }
      }
    }

    /* ---------------- sticky mobile ATC bar ----------------
       Revealed only after the buy box has scrolled fully above the
       viewport (§3: "appears after scrolling past the buy box"); hidden
       again when the buy box scrolls back in. CSS hides the bar ≥990.
       Accordions are <details>-backed and owned by assets/accordion.js. */
    if (stickyBar && buybox) {
      if ('IntersectionObserver' in window) {
        var stickyIo = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            var passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            stickyBar.classList.toggle('is-visible', passed);
          });
        });
        stickyIo.observe(buybox);
      } else {
        stickyBar.classList.add('is-visible');
      }
    }

    /* ---------------- init ---------------- */
    if (finishPos) filterMedia();
    if (current) updateBuyState();
  }

  function boot(scope) {
    Array.prototype.slice.call((scope || document).querySelectorAll('[data-pdp-root]')).forEach(initPdp);
  }

  ready(function () { boot(document); });

  /* theme editor: re-init when the section is re-rendered */
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) boot(e.target);
  });
})();
